import { useMemo } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { issueTone, needsAttention } from "./digestUtils";
import {
  applyCoverageRule,
  isWellnessKind,
  wellnessChipText,
  type WellnessAlert,
} from "./wellnessAttention";
import type { AttentionIssueKind } from "./digestUtils";
import Card from "../../components/ui/Card";
import StatusDot from "../../components/ui/StatusDot";
import TrendBadge from "../../components/ui/TrendBadge";
import PriorityBadge from "../../components/ui/PriorityBadge";
import {
  formatOfflineDuration,
  formatOfflineSinceTooltip,
} from "../../services/offlineFormat";

/** Loosely-typed row from unitEventsTable's TableData (untyped JS). */
type SensorRow = {
  SensorID: string;
  UnitNumber: string;
  PriorityScore?: number | null;
  PriorityBand?: string | null;
  TrendLevel?: number | null;
  Alerts?: number;
  Shutdowns?: number;
  Status?: string; // "Online" | "Offline"
  isPCOconnected?: string; // "YES" | "NO"
  OfflineSinceTs?: number | null;
};

type Issue = { kind: AttentionIssueKind; text?: string };

type Props = {
  rows: SensorRow[];
  onSensorClick?: (s: { name: string; displayName: string }) => void;
  /**
   * Wellness alerts for these sensors, keyed by SensorID.
   *
   * Passed in rather than fetched, matching this component's existing contract
   * of rendering only what the table already computed.
   */
  wellnessAlerts?: WellnessAlert[];
};


// Left-to-right chip order within a row: wellness leads (most urgent), then
// trend, then alerts, shutdowns, offline.
const CHIP_ORDER: Record<string, number> = {
  // A resident nobody has seen leads the row: it is the most time-sensitive
  // thing here, and unlike the others it may be an emergency in progress.
  notSeen: -2,
  declining: 0,
  shutdowns: 1,
  alerts: 2,
  noCooking: 2.5,
  offline: 3,
};

/**
 * "Needs attention" feed for a single property's Sensors screen. Shows the most
 * problematic sensors first (priority-ranked), with the reasons as chips.
 * Built from the table's already-computed per-sensor data, so no extra fetching.
 */
export default function SensorAttention({
  rows,
  onSensorClick,
  wellnessAlerts = [],
}: Props) {
  const items = useMemo(() => {
    const wellnessBySensor = new Map<string, WellnessAlert[]>();
    for (const alert of wellnessAlerts) {
      const list = wellnessBySensor.get(alert.scsName);
      if (list) list.push(alert);
      else wellnessBySensor.set(alert.scsName, [alert]);
    }

    return (rows || [])
      .map((r) => {
        const issues: Issue[] = [];

        for (const alert of wellnessBySensor.get(r.SensorID) ?? []) {
          issues.push({ kind: alert.kind, text: wellnessChipText(alert) });
        }
        if (r.Status === "Offline") {
          const text = r.OfflineSinceTs
            ? `Offline · ${formatOfflineDuration(r.OfflineSinceTs)}`
            : "Offline";
          issues.push({ kind: "offline", text });
        }
        if ((r.Alerts ?? 0) > 0) {
          issues.push({
            kind: "alerts",
            text: `${r.Alerts} alert${r.Alerts === 1 ? "" : "s"}`,
          });
        }
        const hasPCO = r.isPCOconnected === "YES";
        if (hasPCO && (r.Shutdowns ?? 0) > 0) {
          issues.push({
            kind: "shutdowns",
            text: `${r.Shutdowns} safety shut off${r.Shutdowns === 1 ? "" : "s"}`,
          });
        }
        if ((r.TrendLevel ?? 0) > 0) {
          issues.push({ kind: "declining" });
        }
        // A sensor we cannot see is not a quiet kitchen. Drop any activity
        // claim on an offline row rather than letting a reader conclude
        // something about the resident from a fact about the hardware.
        const covered = applyCoverageRule(issues);

        // But suppression must not become silence. If wellness WOULD have
        // fired and coverage is the only reason it did not, the row still
        // belongs in the feed carrying its offline chip: a caregiver relying on
        // these alerts needs to know the sensor went dark, or an absence of
        // news reads as good news.
        const wellnessSuppressed = covered.length < issues.length;

        return { row: r, issues: covered, wellnessSuppressed };
      })
      .filter(
        (it) =>
          it.issues.length > 0 &&
          // Wellness bypasses the alarm-derived gate, and must: a property with
          // a clean alarm history scores none/0, which is exactly where a
          // resident nobody has seen for days matters most.
          (it.issues.some((i) => isWellnessKind(i.kind)) ||
            it.wellnessSuppressed ||
            needsAttention(it.row.PriorityBand, it.row.TrendLevel))
      )
      .sort((a, b) => {
        // Wellness first regardless of the alarm-derived priority score, which
        // is zero for a calm property and would otherwise bury these rows.
        const aw =
          a.issues.some((i) => isWellnessKind(i.kind)) || a.wellnessSuppressed
            ? 1
            : 0;
        const bw =
          b.issues.some((i) => isWellnessKind(i.kind)) || b.wellnessSuppressed
            ? 1
            : 0;
        if (aw !== bw) return bw - aw;
        return (b.row.PriorityScore ?? 0) - (a.row.PriorityScore ?? 0);
      });
  }, [rows, wellnessAlerts]);

  const visible = items;

  return (
    <section className="mb-6" aria-label="Sensors needing attention">
      <div className="flex items-center flex-wrap gap-3 mb-3">
        <h2 className="text-xl font-semibold text-white">Needs attention</h2>
        {items.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-chip bg-alert-critical/20 text-alert-critical text-sm font-semibold">
            {items.length}
          </span>
        )}
        <span
          className="text-xs text-ink-3"
          title="Priority ranking uses safety trend over the last 4 weeks, independent of the date picker. Alert and shut-off chips honor the date range."
        >
          Ranked by the last 4 weeks
        </span>
      </div>

      <Card tone="light" padded={false}>
        {items.length === 0 ? (
          <div className="p-6 flex items-center justify-center gap-2 text-ink-2">
            <StatusDot tone="success" />
            <span className="font-medium">
              No sensors need attention at this property.
            </span>
          </div>
        ) : (
          <ul className="divide-y divide-black/5">
            {visible.map(({ row, issues }) => (
              <li key={row.SensorID}>
                <button
                  type="button"
                  onClick={() =>
                    onSensorClick?.({
                      name: row.SensorID,
                      displayName: row.UnitNumber,
                    })
                  }
                  className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-black/[0.03] focus:outline-none focus-visible:bg-black/[0.05] transition-colors"
                >
                  <span className="w-20 shrink-0 flex">
                    <PriorityBadge
                      score={row.PriorityScore}
                      band={row.PriorityBand}
                    />
                  </span>
                  <span className="font-semibold text-ink truncate flex-1 min-w-0">
                    {row.UnitNumber}
                  </span>
                  <span className="flex items-center gap-2 flex-wrap justify-end">
                    {[...issues]
                      .sort(
                        (a, b) =>
                          (CHIP_ORDER[a.kind] ?? 99) - (CHIP_ORDER[b.kind] ?? 99)
                      )
                      .map((issue) =>
                      issue.kind === "declining" ? (
                        <TrendBadge
                          key={issue.kind}
                          level={row.TrendLevel ?? undefined}
                          size={16}
                        />
                      ) : (
                        <span
                          key={issue.kind}
                          className="inline-flex items-center gap-1.5 rounded-chip bg-black/5 px-2.5 py-1 text-xs font-semibold text-ink-2"
                          title={
                            issue.kind === "offline" && row.OfflineSinceTs
                              ? formatOfflineSinceTooltip(row.OfflineSinceTs)
                              : undefined
                          }
                        >
                          <StatusDot tone={issueTone(issue.kind)} />
                          {issue.text}
                        </span>
                      )
                    )}
                  </span>
                  <ChevronRightIcon
                    className="text-ink-3 shrink-0"
                    fontSize="small"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
